<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Left Text-->
      <b-col
        lg="7"
        class="d-none d-lg-flex align-items-center p-0 only-desktop"
      >
        <div class="w-100 h-100 d-lg-flex bg-login">
          <div
            class="h-100 d-lg-flex flex-column align-items-start justify-content-between p-3"
          >
            <futuro-logo-and-text height="150" dark />
            <h1 class="login-light-title">
              Seja bem-vindo à maior plataforma de planejadores financeiros
              autônomos do Brasil!
            </h1>
          </div>
        </div>
      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col
        cols="12"
        lg="5"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
        v-if="!twoFactorRequired"
      >
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <b-row class="align-items-center justify-content-center pb-2">
            <b-col cols="auto">
              <futuro-text width="150" />
            </b-col>
          </b-row>
          <b-card-title class="mb-1 font-weight-bold" title-tag="h1">
            Login
          </b-card-title>
          <b-card-text class="mb-2">
            Para acessar sua conta, digite seu e-mail e senha no formulário
            abaixo:
          </b-card-text>

          <!-- form -->
          <b-form @submit.prevent="onSubmit">
            <!-- email -->
            <b-form-group label="E-mail" label-for="login-email" class="mb-1">
              <b-form-input
                id="login-email"
                v-model="email"
                name="login-email"
                placeholder="E-mail"
                :class="{ 'is-invalid': v$.email.$error }"
              />
              <div class="invalid-feedback">
                <span v-if="v$.email.$invalid">
                  Você deve informar um email válido
                </span>
              </div>
            </b-form-group>

            <!-- forgot password -->
            <b-form-group class="mb-1">
              <div class="d-flex justify-content-between">
                <label for="login-password">Password</label>
                <b-link :to="{ name: 'account-forgot-password' }">
                  <small>Esqueceu sua senha?</small>
                </b-link>
              </div>
              <b-input-group class="input-group-merge">
                <b-form-input
                  id="login-password"
                  v-model="password"
                  class="form-control-merge"
                  :type="passwordFieldType"
                  :class="{ 'is-invalid': v$.password.$error }"
                  name="login-password"
                  placeholder="Senha"
                />
                <b-input-group-append is-text>
                  <feather-icon
                    class="cursor-pointer"
                    :icon="passwordToggleIcon"
                    @click="togglePasswordVisibility"
                  />
                </b-input-group-append>
              </b-input-group>
              <small
                v-if="v$.password.$error && v$.email.$invalid"
                class="text-danger"
              >
                Você deve informar uma senha válida com pelo menos 4 caracteres
              </small>
            </b-form-group>

            <b-form-group class="mb-1">
              <b-form-checkbox id="remember-me" v-model="rememberMe">
                Lembrar minha senha
              </b-form-checkbox>
            </b-form-group>

            <!-- submit buttons -->
            <div v-if="loginError" class="text-danger mb-1">
              Suas credenciais são inválidas. Verifique seu e-mail e senha e
              tente novamente. Se o erro persistir, entre em contato com o setor
              de TI.
            </div>
            <b-button type="submit" variant="primary" block :disabled="loading">
              {{ loading ? "Aguarde..." : "Entrar" }}
            </b-button>
          </b-form>

          <!-- social buttons -->
          <div class="auth-footer-btn d-flex justify-content-center mt-3">
            <b-button
              variant="instagram"
              :href="socialLinks.instagram"
              target="_blank"
            >
              <feather-icon icon="InstagramIcon" />
            </b-button>
            <b-button
              variant="linkedin"
              :href="socialLinks.linkedin"
              target="_blank"
            >
              <feather-icon icon="LinkedinIcon" />
            </b-button>
            <b-button
              variant="youtube"
              :href="socialLinks.youtube"
              target="_blank"
            >
              <feather-icon icon="YoutubeIcon" />
            </b-button>
            <b-button
              variant="facebook"
              :href="socialLinks.facebook"
              target="_blank"
            >
              <feather-icon icon="FacebookIcon" />
            </b-button>
          </div>
        </b-col>
      </b-col>
      <b-col
        cols="12"
        lg="5"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
        v-else
      >
        <b-card class="shadow-sm">

          <b-card-title class="mb-1 font-weight-bold" title-tag="h1">
            Agora... o código de verificação
          </b-card-title>
          <b-card-text class="mb-2">
            Para a segurança da sua conta, enviamos um código de autorização de 6 (seis)
            dígitos por e-mail. Informe o código recebido para continuar:
          </b-card-text>
          <b-form @submit.prevent="onSubmit" class="mb-3">
            <b-form-group label="" label-for="otp-code">
              <div class="d-flex justify-content-center gap-2 mb-3">
                <input
                  v-for="index in 6"
                  :key="index"
                  :ref="'digit' + index"
                  v-model="code[index - 1]"
                  class="form-control text-center otp-input"
                  type="text"
                  maxlength="1"
                  @input="onInput($event, index)"
                  @keydown="onKeydown($event, index)"
                  @paste="onPaste($event, index)"
                />
              </div>
            </b-form-group>

            <div class="mt-4">
              <b-button
                type="submit"
                variant="primary"
                block
                :disabled="!isCodeComplete || loading"
              >
                {{ loading ? "Aguarde..." : "Verificar código" }}
              </b-button>

              <b-button
                variant="link"
                block
                :disabled="!resendEnabled"
                @click="resendCode"
                class="mt-3"
              >
                {{ resendButtonLabel }}
              </b-button>
            </div>
            <div v-if="loginError" class="text-danger mb-1 mt-2">
              Código inválido. Verifique o último código enviado por e-mail e
              tente novamente. Se o erro persistir, entre em contato com o nosso suporte.
            </div>
          </b-form>
        </b-card>
      </b-col>
      <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import FuturoLogoAndText from "@core/layouts/components/LogoAndText.vue";
import FuturoLogo from "@core/layouts/components/Logo.vue";
import FuturoText from "@core/layouts/components/LogoText.vue";
import {
  BRow,
  BCol,
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroup,
  BFormCheckbox,
  BCardText,
  BCardTitle,
  BImg,
  BForm,
  BButton,
  BAlert,
  VBTooltip,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import useVuelidate from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import { useToast } from "vue-toastification/composition";
import * as types from "../store/types";
import { code } from "@/views/forms/form-element/date-time-picker/code";
import { onKeyDown } from "@vueuse/core";

export default {
  directives: {
    "b-tooltip": VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BAlert,
    FuturoLogoAndText,
    FuturoLogo,
    FuturoText,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      socialLinks: {
        instagram: process.env.VUE_APP_SOCIAL_INSTAGRAM,
        linkedin: process.env.VUE_APP_SOCIAL_LINKEDIN,
        facebook: process.env.VUE_APP_SOCIAL_FACEBOOK,
        youtube: process.env.VUE_APP_SOCIAL_YOUTUBE,
      },
      email: "",
      password: "",
      rememberMe: false,
      loading: false,
      loginError: false,
      twoFactorRequired: false,
      code: Array(6).fill(""),
      resendEnabled: false,
      countdown: 60,
    };
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  validations() {
    return {
      email: { required, email },
      password: {
        required,
        minLength(value) {
          return value.length >= 4;
        },
      },
      rememberMe: {},
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    resendButtonLabel() {
      if (this.resendEnabled) {
        return "Enviar novo código";
      }
      return `Reenviar código (${this.countdown}s)`;
    },
    isCodeComplete() {
      return this.code.every((digit) => digit !== "" && /^\d$/.test(digit));
    },
  },
  methods: {
    ...mapActions({
      login: types.LOGIN,
    }),
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      this.loading = true;
      this.loginError = false;
      const { email, password, rememberMe } = this;
      const auth_code = this.code.join("");
      this.login({ email, password, rememberMe, auth_code })
        .then((response) => {
          if (response.data?.status === '2FA_REQUIRED') {
            this.twoFactorRequired = true;
            this.startCountdown()
          } 
        })
        .catch((err) => {
          this.loginError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onInput(e, index) {
      const value = e.target.value;
      if (!/^\d?$/.test(value)) {
        this.$set(this.code, index - 1, "");
        return;
      }
      this.$set(this.code, index - 1, value);
      if (value && index < 6) {
        this.$refs["digit" + (index + 1)][0].focus();
      }
    },
    onKeydown(e, index) {
      if (e.key === "Backspace" && !this.code[index - 1] && index > 1) {
        this.$refs["digit" + (index - 1)][0].focus();
      }
    },
    onPaste(e, index) {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      const digits = pastedData.replace(/\D/g, '').slice(0, 6);
      
      if (digits.length > 0) {
        const newCode = [...this.code];
        for (let i = 0; i < digits.length && (index - 1 + i) < 6; i++) {
          newCode[index - 1 + i] = digits[i];
        }
        this.code = newCode;
        const nextIndex = Math.min(index - 1 + digits.length, 5);
        this.$nextTick(() => {
          if (nextIndex < 6) {
            this.$refs["digit" + (nextIndex + 1)][0].focus();
          }
        });
      }
    },
    startCountdown() {
      this.countdown = 60;
      this.resendEnabled = false;

      const timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.resendEnabled = true;
          clearInterval(timer);
        }
      }, 1000);
    },
    resendCode(){
      this.loading = true;
      const { email, password, rememberMe } = this;
      this.login({ email, password, rememberMe })
        .then((response) => {
          if (response.data?.status === '2FA_REQUIRED') {
            this.twoFactorRequired = true;
            this.code = Array(6).fill("");
            this.loginError = false;
            this.startCountdown()
          } 
        })
        .catch((err) => {
          this.loginError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
.bg-login {
  background-image: url("https://futurogfp-assets.s3.sa-east-1.amazonaws.com/futuro_background.webp");
  background-size: cover;
}
.login-light-title {
  color: #fff !important;
  margin-bottom: 2rem;
}
@media (max-width: 1024px) {
  .only-desktop {
    display: none;
  }
}

.expired-logo {
  text-align: center;
  margin-top: 2rem;
  z-index: 1000;

  .logo-cont {
    margin-right: 0.5rem;
  }
}

.otp-input {
  width: 40px;
  height: 48px;
  font-size: 1.2rem;
  text-align: center;
  border-radius: 8px;
  border: 1px solid #ced4da;
}

.otp-input:focus {
  border-color: #f26237;
  box-shadow: 0 0 0 0.2rem rgba(242, 98, 55, 0.25);
}

.gap-2 {
  gap: 0.5rem;
}

.shadow-sm {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.text-muted {
  color: #6c757d;
}
</style>
